import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChefHat, Home, Calendar, ShoppingCart, BookOpen, Utensils, Gift, Settings, Shield, Heart, Baby, Menu, X, LogOut } from 'lucide-react';
import useStore from '../store';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import PWAInstallPrompt from './PWAInstallPrompt';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useStore();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (!user) {
        try {
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(response.data);
          setIsAdmin(response.data.role === 'admin' || response.data.role === 'superadmin' || response.data.role === 'staff');
        } catch (error) {
          navigate('/login');
        }
      } else {
        setIsAdmin(user.role === 'admin' || user.role === 'superadmin' || user.role === 'staff');
      }
    };
    fetchUser();
  }, [user, setUser, navigate]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { path: '/dashboard', icon: Home, labelKey: 'nav.dashboard', dataTestId: 'nav-dashboard', alwaysShow: true },
    { path: '/plans', icon: Calendar, labelKey: 'nav.plans', dataTestId: 'nav-plans', alwaysShow: true },
    { path: '/recipes', icon: BookOpen, labelKey: 'nav.recipes', dataTestId: 'nav-recipes', flag: 'enable_community_recipes' },
    { path: '/slim-uit-eten', icon: Utensils, labelKey: 'nav.slim_uit_eten', dataTestId: 'nav-eat-out', flag: 'enable_slim_uit_eten' },
    { path: '/favorites', icon: Heart, labelKey: 'nav.favorites', dataTestId: 'nav-favorites', alwaysShow: true },
    { path: '/baby-mode', icon: Baby, labelKey: 'nav.baby_mode', dataTestId: 'nav-baby-mode', alwaysShow: true },
    { path: '/points', icon: Gift, labelKey: 'nav.points', dataTestId: 'nav-points', flag: 'enable_points_system' },
    { path: '/settings', icon: Settings, labelKey: 'nav.settings', dataTestId: 'nav-settings', alwaysShow: true }
  ];

  if (isAdmin) {
    menuItems.push({ path: '/admin', icon: Shield, labelKey: 'nav.admin', dataTestId: 'nav-admin', alwaysShow: true });
  }

  const visibleMenuItems = menuItems.filter(item => {
    if (item.alwaysShow) return true;
    if (!item.flag) return true;
    return isEnabled(item.flag);
  });

  // Bottom tab items for mobile (key pages only)
  const bottomTabs = visibleMenuItems.filter(item =>
    ['/dashboard', '/plans', '/recipes', '/favorites', '/settings'].includes(item.path)
  );

  const isActive = (path) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    navigate('/login');
  };

  if (flagsLoading) {
    return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Desktop top nav */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-30 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="app-logo">
              <div className="bg-primary rounded-full p-2">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-bold text-primary">FamilyPlate</span>
            </div>

            <div className="flex items-center gap-1">
              {visibleMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      active ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={item.dataTestId}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{t(item.labelKey)}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={handleLogout}
              className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors ml-auto"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile top bar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-30 md:hidden">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="app-logo-mobile">
            <div className="bg-primary rounded-full p-1.5">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-heading font-bold text-primary">FamilyPlate</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-14 z-20 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative bg-white border-b border-gray-200 shadow-lg max-h-[70vh] overflow-y-auto">
            <div className="p-3 space-y-1">
              {visibleMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors ${
                      active ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-${item.dataTestId}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-base font-medium">{t(item.labelKey)}</span>
                  </button>
                );
              })}
              <hr className="my-2" />
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-base font-medium">{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content - add bottom padding on mobile for tab bar */}
      <main className="flex-1 pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Mobile bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around h-16 pb-1">
          {bottomTabs.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 min-w-0 flex-1 ${
                  active ? 'text-primary' : 'text-gray-400'
                }`}
                data-testid={`tab-${item.dataTestId}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px] font-medium truncate w-full text-center">{t(item.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default AppLayout;
